import { getLineType } from "../components/ruleLineType";

// ── Subtype display framework ────────────────────────────────────────────────
// rule_subtype_id semantics:
//   1 / 4 → Value Change
//   2 / 5 → Percent Change
//   3 / 6 → Hits Value
//
// Each entry receives the raw rule_details.details so the description function
// can reference thresholds, directions, etc. once the user fills in the text.

export function resolveTemplate(
  template: string,
  values: Record<string, unknown>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const val = values[key];
    return val !== undefined && val !== null ? String(val) : `{${key}}`;
  });
}

export const SUBTYPE_DISPLAY: Record<
  number,
  { label: string; template: string }
> = {
  1: {
    label: "Value Change",
    template:
      "When the {rule_group_description} {rule_definition_description} changes by {value_change} from the Start of the Game",
  },
  2: {
    label: "Percent Change",
    template:
      "When the {rule_group_description} {rule_definition_description} changes by {percent}% from the Start of the Game",
  },
  3: {
    label: "Hits Value",
    template:
      "If the {rule_group_description} {rule_definition_description} is {equality} {value}",
  },
  // Team-level variants of 1-3 (GenericTeam* in RuleLogic.tsx).
  4: {
    label: "Value Change",
    template:
      "When a team's {rule_group_description} {rule_definition_description} changes by {value_change} from the Start of the Game",
  },
  5: {
    label: "Percent Change",
    template:
      "When a team's {rule_group_description} {rule_definition_description} changes by {percent}% from the Start of the Game",
  },
  6: {
    label: "Hits Value",
    template:
      "If a team's {rule_group_description} {rule_definition_description} is {equality} {value}",
  },
};

const signed = (val: unknown, decimals?: number): unknown => {
  if (typeof val !== "number") return val;
  const text = decimals != null ? val.toFixed(decimals) : String(val);
  return val > 0 ? `+${text}` : text;
};

const oneDecimal = (val: unknown): unknown =>
  typeof val === "number" ? val.toFixed(1) : val;

// Formats the saved numbers the way the rule form showed them when the rule
// was built (same line-type detection - see ruleLineType.ts): Moneyline-style
// values are signed whole numbers (+150), Spread targets are signed with one
// decimal (+3.5), every other target is a Line / Over/Under value with one
// decimal (230.0), and changes are always signed since they can go either way.
export function formatDetailValues(
  details: Record<string, unknown>,
): Record<string, unknown> {
  const lineType = getLineType(
    `${(details["rule_group_description"] as string) ?? ""} ${
      (details["rule_definition_description"] as string) ?? ""
    }`,
  );
  return {
    ...details,
    value:
      lineType === "moneyline"
        ? signed(details["value"], 0)
        : lineType === "spread"
          ? signed(details["value"], 1)
          : oneDecimal(details["value"]),
    value_change: signed(
      details["value_change"],
      lineType === "moneyline" ? 0 : 1,
    ),
    percent: signed(details["percent"]),
  };
}
